import { FC, useCallback } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '../../../ui/feedback/Dialog';
import { getIsMobile } from '../../../utils/getIsMobile';
import { useTranslation } from '../../../utils/useTranslation';
import { useDialogScroll } from '../../feedback/hooks/useDialogScroll';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Flex } from '../../layout/Flex';
import { CollectionInfoContent } from '../CollectionInfoContent/CollectionInfoContent';
import { CpaTrialDisclaimer } from '../CpaTrialDisclaimer/CpaTrialDisclaimer';
import { ActionButton } from '../styles';
import { ExistentCollectionContent } from './parts/ExistentCollectionContent/ExistentCollectionContent';
import { TitleContent } from './parts/TitleContent/TitleContent';
import { BookmarkStep, SaveToCollectionDrawerProps } from './types';

export const SaveToCollectionDrawer: FC<SaveToCollectionDrawerProps> = ({
    collections,
    isCollectionsLoading,
    onClose,
    onSaveToExistentCollection,
    values,
    handlers,
    step,
    isNotRegistered,
    onAddClick,
}) => {
    const t = useTranslation();

    const isMobile = getIsMobile().isMobile;

    const { modalContentRef, atTop, atBottom } = useDialogScroll();

    const onSaveToExistentCollectionCallback = useCallback(
        (collectionId: string) => {
            onSaveToExistentCollection(collectionId);
        },
        [onSaveToExistentCollection],
    );

    const mainContent = () => {
        if (step === BookmarkStep.ADD_TO_EXIST_COLLECTION) {
            return (
                <ExistentCollectionContent
                    onClose={onClose}
                    collections={collections}
                    isCollectionsLoading={isCollectionsLoading}
                    onAddClick={onAddClick}
                    onSaveClick={onSaveToExistentCollectionCallback}
                />
            );
        }
        if (step === BookmarkStep.ADD_TO_NEW_COLLECTION) {
            return <CollectionInfoContent values={values} handlers={handlers} />;
        }
    };

    const enabledSaveCollection =
        values.collectionName || (values.showInFeed && values.currentSuggest);

    const titleContent = () => <TitleContent step={step} />;

    const footerContent = () => (
        <Flex direction="column" width="100%" pt={isMobile ? 1 : 0}>
            <ActionButton
                dataTestId={DataTestId.SAVE_TO_NEW_COLLECTION_BUTTON}
                minHeight="46px"
                onClick={handlers.onSubmit}
                disabled={!enabledSaveCollection}
            >
                {t('Создать')}
            </ActionButton>
            {isNotRegistered ? <CpaTrialDisclaimer /> : null}
        </Flex>
    );

    return (
        <Dialog onClose={onClose} dataTestId={DataTestId.SAVE_TO_COLLECTION_DRAWER}>
            <DialogTitle withBorderBottom={!atTop} onClose={onClose}>
                {titleContent()}
            </DialogTitle>
            <DialogContent ref={modalContentRef} py={0} px={0}>
                {mainContent()}
            </DialogContent>
            {step !== BookmarkStep.ADD_TO_EXIST_COLLECTION && (
                <DialogFooter withBorderTop={!atBottom}>{footerContent()}</DialogFooter>
            )}
        </Dialog>
    );
};
